import { create } from 'zustand'
import { notificationsAPI } from '../services/api'

/**
 * useNotificationStore v163.6.0
 * 
 * Global notification state with:
 * - unreadCount tracked across the app
 * - fetchUnreadCount() callable from any screen
 * - startPolling / stopPolling for background refresh
 * - visibilitychange listener for tab-focus refresh
 * 
 * Polling interval: 30s when app is visible, paused when hidden.
 */

let pollInterval = null
let visibilityHandler = null

export const useNotificationStore = create((set, get) => ({
  unreadCount: 0,
  lastFetched: null,
  // s101: the What's new notice is unread on this device (utils/whatsNew.js noticeNotes). Kept apart
  //   from unreadCount, which is the server's count and which "mark all read" clears.
  whatsNewUnread: false,
  setWhatsNewUnread: (v) => set({ whatsNewUnread: !!v }),

  fetchUnreadCount: async () => {
    try {
      const result = await notificationsAPI.list()
      const notifications = result.notifications || []
      const unread = notifications.filter(n => !n.read).length
      set({ unreadCount: unread, lastFetched: Date.now() })
      return unread
    } catch (err) {
      console.error('[NotifStore] Failed to fetch unread count:', err)
      return get().unreadCount
    }
  },

  decrementUnread: () => set(s => ({ unreadCount: Math.max(0, s.unreadCount - 1) })),
  clearUnread: () => set({ unreadCount: 0 }),

  // v242.40.0: reset on account switch / logout so account A's unread badge
  // doesn't bleed onto account B; polling re-arms on the next account's mount.
  resetForUser: () => {
    try { get().stopPolling() } catch (_) {}
    set({ unreadCount: 0, lastFetched: null })
  },

  startPolling: () => {
    const { fetchUnreadCount } = get()

    // Initial fetch
    fetchUnreadCount()

    // Poll every 30s
    if (pollInterval) clearInterval(pollInterval)
    pollInterval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchUnreadCount()
      }
    }, 30000)

    // Refresh on tab focus / app resume
    if (visibilityHandler) document.removeEventListener('visibilitychange', visibilityHandler)
    visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        const last = get().lastFetched
        // Only refetch if >5s since last fetch (debounce rapid switches)
        if (!last || Date.now() - last > 5000) {
          fetchUnreadCount()
        }
      }
    }
    document.addEventListener('visibilitychange', visibilityHandler)
  },

  stopPolling: () => {
    if (pollInterval) { clearInterval(pollInterval); pollInterval = null }
    if (visibilityHandler) { document.removeEventListener('visibilitychange', visibilityHandler); visibilityHandler = null }
  },
}))

export default useNotificationStore
